import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCreditCard, faPrint, faSpinner, faTimes, faUser, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import '../../css/JuanScope/ScopeStudentAssesment.css';
import useEnrollmentStatus from '../../hooks/userEnrollmentStatus';
import SJDEFILogo from '../../images/SJDEFILogo.png';
import SideNavigation from './SideNavigation';
import { createTuitionPayment } from '../../api/applicant';
import { notification } from 'antd';
import axios from 'axios';

function ScopeStudentAssessment() {
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [isFeeLoading, setIsFeeLoading] = useState(true);
  const [feeError, setFeeError] = useState('');

  // State for Class Schedule
  const [classSchedule, setClassSchedule] = useState(null);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState('');

  const [expandedSections, setExpandedSections] = useState({
    tuition: true,
    miscellaneous: true,
    otherFees: true,
    installment: true,
    schedule: true,
  });

  const { userData, applicationStatus, loading, error, handleLogout } = useEnrollmentStatus();

  // Helper to format academic year
  const getFormattedAcademicYear = (ay) => {
    if (!ay) return 'AY 2025-2026';
    if (ay.startsWith('AY ')) return ay;
    return `AY ${ay}`;
  };

  // Map voucher type to category
  const getCategory = (voucherType) => {
    switch (voucherType) {
      case 'PUBLIC SCHOOL VOUCHER':
        return 'Public School';
      case 'PEAC VOUCHER':
        return 'Private with PEAC Voucher';
      case 'PRIVATE SCHOOL WITHOUT VOUCHER':
        return 'Private without Voucher';
      default:
        return null;
    }
  };

  // Calculate reservation fee amount paid
  const reservationFeeAmountPaid = useMemo(() => {
    if (!userData || !userData.reservationFeeAmountPaid) return 0;
    return typeof userData.reservationFeeAmountPaid === 'string'
      ? parseFloat(userData.reservationFeeAmountPaid.replace(/[₱,]/g, '')) || 0
      : userData.reservationFeeAmountPaid || 0;
  }, [userData]);

  // Fetch tuition details
  useEffect(() => {
    const fetchAllTuitionDetails = async () => {
      if (!userData) return;
      setIsFeeLoading(true);
      setFeeError('');
      try {
        const category = getCategory(userData.voucherType);
        const semester = userData.academicTerm?.includes('1st') ? '1st Semester' : '2nd Semester';
        const academicYear = getFormattedAcademicYear(userData.academicYear);
        const isInstallmentAllowed = userData.voucherType !== 'PUBLIC SCHOOL VOUCHER';

        const fullPaymentPromise = axios.get(`${process.env.REACT_APP_API_URL}/api/tuition-details`, {
          params: { category, semester, paymentType: 'Full Payment', academicYear },
        });

        const installmentPromise = isInstallmentAllowed
          ? axios.get(`${process.env.REACT_APP_API_URL}/api/tuition-details`, {
              params: { category, semester, paymentType: 'Installment', academicYear },
            })
          : Promise.resolve(null);

        const [fullPaymentResponse, installmentResponse] = await Promise.all([
          fullPaymentPromise,
          installmentPromise,
        ]);

        const combinedFeeData = fullPaymentResponse.data;
        if (installmentResponse && installmentResponse.data) {
          combinedFeeData.fees.installment = installmentResponse.data.fees.installment;
        }

        setFeeData(combinedFeeData);
      } catch (err) {
        console.error('Error fetching tuition details:', err.response || err);
        setFeeError(err.response?.data?.error || 'Tuition details not found. Please check your enrollment data.');
      } finally {
        setIsFeeLoading(false);
      }
    };
    fetchAllTuitionDetails();
  }, [userData]);

  // CORRECTED: Fetch Class Schedule using the new dedicated route
  useEffect(() => {
    const fetchClassSchedule = async () => {
      if (!userData || !userData.email || !userData.preferredSection || userData.preferredSection === 'Not yet assigned') {
        setIsScheduleLoading(false);
        return;
      }
  
      setIsScheduleLoading(true);
      setScheduleError('');
  
      try {
        // Call the new, secure endpoint for students
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/student/schedules/my-schedule`, {
          params: { email: userData.email }
        });
  
        if (response.data && response.data.success) {
          setClassSchedule(response.data.data);
        } else {
          setScheduleError(response.data.message || 'Could not retrieve schedule.');
        }
      } catch (err) {
        console.error('Error fetching class schedule:', err.response || err);
        setScheduleError(err.response?.data?.message || 'Failed to load class schedule. Please try again later.');
      } finally {
        setIsScheduleLoading(false);
      }
    };
  
    fetchClassSchedule();
  }, [userData]);

  // Calculate fees with reservation fee deduction
  const calculatedFees = useMemo(() => {
    if (!feeData || !userData) return null;

    const installmentPlan = feeData.fees.installment || {};
    const installmentTotalBeforeDeduction = installmentPlan.installmentTotal || 0;
    const finalInstallmentTotal = Math.max(0, installmentTotalBeforeDeduction - reservationFeeAmountPaid);

    const updatedBreakdown = installmentPlan.breakdown?.map((item, index) => {
      if (index === 0 && reservationFeeAmountPaid > 0) {
        return {
          ...item,
          amount: Math.max(0, item.amount - reservationFeeAmountPaid),
        };
      }
      return item;
    }) || [];

    return {
      ...feeData.fees,
      reservationFeeDeduction: reservationFeeAmountPaid,
      finalTotal: Math.max(0, feeData.fees.finalTotal - reservationFeeAmountPaid),
      semester: feeData.semester,
      category: feeData.category,
      installment: {
        ...installmentPlan,
        installmentTotal: finalInstallmentTotal,
        breakdown: updatedBreakdown,
      },
    };
  }, [feeData, userData, reservationFeeAmountPaid]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const handlePrint = () => {
    window.print();
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleProceedPayment = async () => {
    if (!calculatedFees || !userData) {
      notification.error({
        message: 'Error',
        description: 'Fee calculation error. Please refresh the page.',
        placement: 'topRight',
      });
      return;
    }

    const isInstallmentAllowed = userData.voucherType !== 'PUBLIC SCHOOL VOUCHER';
    const isZeroAmount = calculatedFees.finalTotal === 0;

    const installmentPlan = calculatedFees.installment;

    const paymentModes = {
      fullPayment: {
        amount: calculatedFees.finalTotal,
        description: 'Pay the full amount at once',
      },
    };

    if (isInstallmentAllowed && !isZeroAmount && installmentPlan?.installmentTotal > 0) {
      paymentModes.installmentPayment = {
        amount: installmentPlan.installmentTotal,
        originalAmount: calculatedFees.finalTotal,
        increasePercent: installmentPlan.increasePercent * 100,
        description: `Pay in 3 installments with a ${installmentPlan.increasePercent * 100}% increase.`,
      };
    }

    const paymentData = {
      studentInfo: {
        fullName: `${userData.firstName || ''} ${userData.middleName || ''} ${userData.lastName || ''}`.trim() || 'N/A',
        studentID: userData.studentID || 'N/A',
        dob: userData.dob,
        gender: userData.gender || 'N/A',
        email: userData.email?.toLowerCase() || 'N/A',
        mobile: userData.mobile || 'N/A',
        academicYear: getFormattedAcademicYear(userData.academicYear),
        academicTerm: userData.academicTerm || 'N/A',
        academicStrand: userData.academicStrand || 'N/A',
        academicLevel: userData.academicLevel || 'N/A',
        voucherType: userData.voucherType || 'N/A',
        hasReservation: userData.reservationFeePaymentStepStatus === 'Complete',
      },
      tuitionAndFees: {
        tuition: calculatedFees.tuition.finalAmount || 0,
        miscellaneous: calculatedFees.miscellaneous.finalTotal || 0,
        otherFees: calculatedFees.otherFees.finalTotal || 0,
        totalAmount: calculatedFees.finalTotal || 0,
        discount: calculatedFees.discount || 0,
        reservationFeeDeduction: calculatedFees.reservationFeeDeduction || 0,
        voucherType: userData.voucherType,
        semester: calculatedFees.semester,
      },
      paymentMode: 'Full Payment',
      paymentMethod: 'none',
      paymentAmount: calculatedFees.finalTotal || 0,
      isFullPayment: true,
      installmentPlan: [],
      remainingPayments: [],
      agreeToTerms: true,
      timestamp: new Date().toISOString(),
      paymentType: 'full',
    };

    if (isZeroAmount) {
      try {
        notification.info({
          message: 'Processing Payment',
          description: 'Recording your payment status, please wait...',
          placement: 'topRight',
          duration: 3,
        });

        const result = await createTuitionPayment(paymentData);

        if (result.success) {
          localStorage.setItem('paymentCompleted', 'true');
          notification.success({
            message: 'Payment Recorded',
            description: 'No payment is required as the amount due is zero. Your payment status has been recorded.',
            placement: 'topRight',
            duration: 4,
          });

          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        } else {
          throw new Error(result.message || 'Failed to record payment');
        }
      } catch (error) {
        notification.error({
          message: 'Payment Recording Failed',
          description: error.message || 'An unexpected error occurred. Please try again.',
          placement: 'topRight',
          duration: 6,
        });
      }
    } else {
      navigate('/select-payment-method', {
        state: {
          studentInfo: paymentData.studentInfo,
          tuitionAndFees: paymentData.tuitionAndFees,
          paymentModes,
          installmentPlan: installmentPlan?.breakdown || [],
          paymentStep: true,
        },
      });

      localStorage.setItem('proceedToPaymentStep', 'true');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return `₱${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const numberToWords = (num = 0) => {
    const units = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
    const thousands = ['', 'THOUSAND', 'MILLION', 'BILLION'];

    if (num === 0) return 'ZERO';

    const convertChunk = (n, level) => {
      let str = '';
      if (n >= 100) {
        str += `${units[Math.floor(n / 100)]} HUNDRED `;
        n %= 100;
      }
      if (n >= 20) {
        str += `${tens[Math.floor(n / 10)]} `;
        n %= 10;
      } else if (n >= 10) {
        str += `${teens[n - 10]} `;
        n = 0;
      }
      if (n > 0 && n < 10) {
        str += `${units[n]} `;
      }
      return str + (n > 0 || str ? thousands[level] : '');
    };

    let [integer, decimal] = num.toFixed(2).split('.');
    integer = parseInt(integer, 10);
    decimal = parseInt(decimal, 10);

    let words = '';
    let level = 0;
    while (integer > 0) {
      const chunk = integer % 1000;
      if (chunk > 0) {
        words = `${convertChunk(chunk, level)} ${words}`.trim();
      }
      integer = Math.floor(integer / 1000);
      level++;
    }

    if (decimal > 0) {
      words += ` AND ${convertChunk(decimal, 0)}/100`;
    } else {
      words += ' AND 00/100';
    }

    return words.trim();
  };

  return (
    <div className="scope-assessment-container">
      <header className="juan-register-header">
        <div className="juan-header-left">
          <img src={SJDEFILogo} alt="SJDEFI Logo" className="juan-logo-register" />
          <div className="juan-header-text">
            <h1>JUAN SCOPE</h1>
          </div>
        </div>
        <div className="hamburger-menu">
          <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle navigation menu">
            <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} size="lg" />
          </button>
        </div>
      </header>

      <div className="scope-assessment-content">
        <SideNavigation
          userData={userData}
          onNavigate={closeSidebar}
          isOpen={sidebarOpen}
          {...applicationStatus}
        />

        <main className={`scope-assessment-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          {loading || isFeeLoading ? (
            <div className="scope-assessment-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading Assessment Details...
            </div>
          ) : error || feeError ? (
            <div className="scope-assessment-error">{error || feeError}</div>
          ) : !calculatedFees ? (
            <div className="scope-assessment-error">Tuition details not found.</div>
          ) : (
            <div className="assessment-content">
              <div className="print-only-header">
                <img src={SJDEFILogo} alt="SJDEFI Logo" className="print-logo" />
                <div className="print-institution-details">
                  <h1>San Juan de Dios Educational Foundation, Inc.</h1>
                  <p>2772-2774 Roxas Boulevard, Pasay City, Metro Manila, Philippines</p>
                  <p>Contact: (02) 8831-2321 | Email: info@sjdefi.edu.ph</p>
                  <p>Assessment Date: {formatDate(new Date())}</p>
                </div>
              </div>
              <h2 className="assessment-title">Student Assessment - Payment Details</h2>
              <div className="assessment-divider"></div>

              {userData && (
                <div className="assessment-card applicant-data-card">
                  <div className="assessment-card-header">
                    <h3>
                      <FontAwesomeIcon icon={faUser} /> Student Information
                    </h3>
                  </div>
                  <div className="assessment-card-body">
                    <div className="applicant-data-grid">
                      <div className="applicant-data-row">
                        <div className="applicant-data-item">
                          <label>Full Name:</label>
                          <span>{`${userData.firstName || ''} ${userData.middleName || ''} ${userData.lastName || ''}`.trim() || 'N/A'}</span>
                        </div>
                        <div className="applicant-data-item">
                          <label>Student ID:</label>
                          <span>{userData.studentID || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="applicant-data-row">
                        <div className="applicant-data-item">
                          <label>Date of Birth:</label>
                          <span>{formatDate(userData.dob)}</span>
                        </div>
                        <div className="applicant-data-item">
                          <label>Gender:</label>
                          <span>{userData.gender || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="applicant-data-row">
                        <div className="applicant-data-item">
                          <label>Email:</label>
                          <span>{userData.email || 'N/A'}</span>
                        </div>
                        <div className="applicant-data-item">
                          <label>Phone:</label>
                          <span>{userData.mobile || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="applicant-data-row">
                        <div className="applicant-data-item">
                          <label>Academic Year:</label>
                          <span>{getFormattedAcademicYear(userData.academicYear)}</span>
                        </div>
                        <div className="applicant-data-item">
                          <label>Academic Term:</label>
                          <span>{userData.academicTerm || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="applicant-data-row">
                        <div className="applicant-data-item">
                          <label>Academic Strand:</label>
                          <span>{userData.academicStrand || 'N/A'}</span>
                        </div>
                        <div className="applicant-data-item">
                          <label>Academic Level:</label>
                          <span>{userData.academicLevel || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="applicant-data-row">
                        <div className="applicant-data-item">
                          <label>Voucher Type:</label>
                          <span>{userData.voucherType || 'N/A'}</span>
                        </div>
                        <div className="applicant-data-item">
                          <label>Assigned Section:</label>
                          <span className={userData.preferredSection && userData.preferredSection !== 'Not yet assigned' ? 'section-assigned' : 'section-not-assigned'}>
                            {userData.preferredSection || 'Not yet assigned'}
                          </span>
                        </div>
                      </div>
                      {userData.reservationFeePaymentStepStatus === 'Complete' && reservationFeeAmountPaid > 0 && (
                        <div className="applicant-data-row">
                          <div className="applicant-data-item">
                            <label>Reservation Fee Amount Paid:</label>
                            <span className="reservation-fee-paid">{formatCurrency(reservationFeeAmountPaid)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {calculatedFees && (
                <div className="assessment-grid">
                  <div className="assessment-card">
                    <div className="assessment-card-header" onClick={() => toggleSection('tuition')}>
                      <h3>
                        TUITION FEES
                        <FontAwesomeIcon icon={expandedSections.tuition ? faChevronUp : faChevronDown} />
                      </h3>
                    </div>
                    {expandedSections.tuition && (
                      <div className="assessment-card-body">
                        <table className="assessment-table">
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Tuition Fee</td>
                              <td className="amount">{formatCurrency(calculatedFees.tuition.finalAmount)}</td>
                            </tr>
                            {calculatedFees.tuition.baseAmount !== calculatedFees.tuition.finalAmount && (
                              <tr>
                                <td>Base Tuition</td>
                                <td className="amount">{formatCurrency(calculatedFees.tuition.baseAmount)}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="assessment-card">
                    <div className="assessment-card-header" onClick={() => toggleSection('miscellaneous')}>
                      <h3>
                        MISCELLANEOUS FEES
                        <FontAwesomeIcon icon={expandedSections.miscellaneous ? faChevronUp : faChevronDown} />
                      </h3>
                    </div>
                    {expandedSections.miscellaneous && (
                      <div className="assessment-card-body">
                        <table className="assessment-table">
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(calculatedFees.miscellaneous).map(([key, value]) => {
                              if (key === 'total' || key === 'finalTotal' || value === 0) return null;
                              return (
                                <tr key={key}>
                                  <td>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</td>
                                  <td className="amount">{formatCurrency(value)}</td>
                                </tr>
                              );
                            })}
                            <tr className="subtotal-row">
                              <td><strong>Total Miscellaneous Fees</strong></td>
                              <td className="amount"><strong>{formatCurrency(calculatedFees.miscellaneous.finalTotal)}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="assessment-card">
                    <div className="assessment-card-header" onClick={() => toggleSection('otherFees')}>
                      <h3>
                        OTHER FEES
                        <FontAwesomeIcon icon={expandedSections.otherFees ? faChevronUp : faChevronDown} />
                      </h3>
                    </div>
                    {expandedSections.otherFees && (
                      <div className="assessment-card-body">
                        <table className="assessment-table">
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(calculatedFees.otherFees).map(([key, value]) => {
                              if (key === 'total' || key === 'finalTotal' || key === 'labFees' || value === 0) return null;
                              if (key === 'energyFee') {
                                return (
                                  <tr key={key}>
                                    <td>Energy Fee</td>
                                    <td className="amount">{formatCurrency(value.finalAmount)}</td>
                                  </tr>
                                );
                              }
                              return (
                                <tr key={key}>
                                  <td>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</td>
                                  <td className="amount">{formatCurrency(value)}</td>
                                </tr>
                              );
                            })}
                            {Object.entries(calculatedFees.otherFees.labFees).map(([key, value]) => {
                              if (value === 0) return null;
                              return (
                                <tr key={key}>
                                  <td>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</td>
                                  <td className="amount">{formatCurrency(value)}</td>
                                </tr>
                              );
                            })}
                            <tr className="subtotal-row">
                              <td><strong>Total Other Fees</strong></td>
                              <td className="amount"><strong>{formatCurrency(calculatedFees.otherFees.finalTotal)}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="assessment-card">
                    <div className="assessment-card-header">
                      <h3>SUMMARY</h3>
                    </div>
                    <div className="assessment-card-body">
                      <table className="assessment-table">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Base Total</td>
                            <td className="amount">{formatCurrency(calculatedFees.baseTotal)}</td>
                          </tr>
                          {calculatedFees.discount > 0 && (
                            <tr>
                              <td>Discount Applied</td>
                              <td className="amount negative">({formatCurrency(calculatedFees.discount)})</td>
                            </tr>
                          )}
                          {calculatedFees.reservationFeeDeduction > 0 && (
                            <tr>
                              <td>Reservation Fee Deduction</td>
                              <td className="amount negative">({formatCurrency(calculatedFees.reservationFeeDeduction)})</td>
                            </tr>
                          )}
                          <tr className="total-row">
                            <td><strong>TOTAL AMOUNT DUE</strong></td>
                            <td className="amount total"><strong>{formatCurrency(calculatedFees.finalTotal)}</strong></td>
                          </tr>
                          <tr>
                            <td><strong>Amount in Words</strong></td>
                            <td>{numberToWords(calculatedFees.finalTotal)}</td>
                          </tr>
                        </tbody>
                      </table>
                      {feeData.notes && (
                        <div className="assessment-note">
                          <p><strong>Note:</strong> {feeData.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {calculatedFees.finalTotal > 0 && (
                    <div className="assessment-card">
                      <div className="assessment-card-header">
                        <h3>MODE OF PAYMENTS</h3>
                      </div>
                      <div className="assessment-card-body">
                        <div className="payment-mode-section">
                          <div className="payment-mode-option">
                            <h4>FULL PAYMENT</h4>
                            <div className="payment-amount">
                              <span>Total Amount:</span>
                              <span className="amount">{formatCurrency(calculatedFees.finalTotal)}</span>
                            </div>
                            <div className="payment-note">
                              <small>Pay the complete amount at once</small>
                            </div>
                          </div>

                          {userData.voucherType !== 'PUBLIC SCHOOL VOUCHER' && calculatedFees.installment?.installmentTotal > 0 && (
                            <>
                              <div className="payment-mode-divider">OR</div>
                              <div className="payment-mode-option">
                                <h4>INSTALLMENT PAYMENT</h4>
                                <div className="payment-amount">
                                  <span>Total Amount (with {calculatedFees.installment.increasePercent * 100}% increase):</span>
                                  <span className="amount">{formatCurrency(calculatedFees.installment.installmentTotal)}</span>
                                </div>
                                <div className="installment-note">
                                  <small>*{calculatedFees.installment.increasePercent * 100}% increase applied for installment payments</small>
                                  <br />
                                  <small>*First payment: Downpayment (1/3 of installment total, adjusted for reservation fee)</small>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {userData.voucherType !== 'PUBLIC SCHOOL VOUCHER' && calculatedFees.installment?.breakdown?.length > 0 && calculatedFees.finalTotal > 0 && (
                    <div className="assessment-card full-width">
                      <div className="assessment-card-header" onClick={() => toggleSection('installment')}>
                        <h3>
                          INSTALLMENT PLAN BREAKDOWN
                          <FontAwesomeIcon icon={expandedSections.installment ? faChevronUp : faChevronDown} />
                        </h3>
                      </div>
                      {expandedSections.installment && (
                        <div className="assessment-card-body">
                          <table className="assessment-installment-table">
                            <thead>
                              <tr>
                                <th>Payment Type</th>
                                <th>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {calculatedFees.installment.breakdown.map((installment, index) => (
                                <tr key={installment.type} className={installment.type === 'TOTAL' ? 'total-row' : ''}>
                                  <td>
                                    {installment.type}
                                    {index === 0 && <span className="current-payment"> (Current Payment)</span>}
                                  </td>
                                  <td className="amount">
                                    {installment.type === 'TOTAL' ? (
                                      <strong>{formatCurrency(installment.amount)}</strong>
                                    ) : (
                                      formatCurrency(installment.amount)
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="installment-breakdown-note">
                            <p><strong>Important Notes:</strong></p>
                            <ul>
                              <li>The first payment (Downpayment) is required for enrollment</li>
                              <li>Remaining payments must be completed by their respective due dates</li>
                              <li>A {calculatedFees.installment.increasePercent * 100}% increase is applied to the total amount for installment payments</li>
                              <li>Each installment is approximately 1/3 of the total installment amount</li>
                              {calculatedFees.reservationFeeDeduction > 0 && (
                                <li key="reservation-deduction">
                                  Reservation fee of {formatCurrency(calculatedFees.reservationFeeDeduction)} has been deducted from the downpayment
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* CORRECTED: Class Schedule Section */}
              {userData && userData.preferredSection && userData.preferredSection !== 'Not yet assigned' && (
                <div className="assessment-card full-width">
                  <div className="assessment-card-header" onClick={() => toggleSection('schedule')}>
                    <h3>
                      CLASS SCHEDULE
                      <FontAwesomeIcon icon={expandedSections.schedule ? faChevronUp : faChevronDown} />
                    </h3>
                  </div>
                  {expandedSections.schedule && (
                    <div className="assessment-card-body">
                      {isScheduleLoading ? (
                        <div className="schedule-loading">
                          <FontAwesomeIcon icon={faSpinner} spin /> Loading Schedule...
                        </div>
                      ) : scheduleError ? (
                        <div className="schedule-error">{scheduleError}</div>
                      ) : classSchedule ? (
                        <table className="assessment-schedule-table">
                          <thead>
                            <tr>
                              <th>Subject Code</th>
                              <th>Subject Name</th>
                              <th>Days</th>
                              <th>Time</th>
                              <th>Room</th>
                              <th>Faculty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {classSchedule.scheduleEntries.map((entry, index) => (
                              <tr key={entry.subjectRef || index}>
                                <td>{entry.subjectCode}</td>
                                <td>{entry.subjectName}</td>
                                <td>{entry.days.join(', ')}</td>
                                <td>{`${entry.timeStart} - ${entry.timeEnd}`}</td>
                                <td>{entry.room}</td>
                                <td>
                                  {entry.facultyRef && entry.facultyRef.firstName && entry.facultyRef.lastName
                                    ? `${entry.facultyRef.firstName} ${entry.facultyRef.lastName}`
                                    : 'TBA'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                         <div className="schedule-error">No schedule information available.</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="assessment-actions">
                <button className="assessment-button print-button" onClick={handlePrint}>
                  <FontAwesomeIcon icon={faPrint} /> Print Assessment
                </button>
                <button className="assessment-button payment-button" onClick={handleProceedPayment} disabled={!calculatedFees}>
                  <FontAwesomeIcon icon={faCreditCard} /> {calculatedFees && calculatedFees.finalTotal === 0 ? 'Confirm Payment Status' : 'Proceed to Payment'}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {sidebarOpen && <div className="scope-assessment-sidebar-overlay active" onClick={toggleSidebar}></div>}

      {showLogoutModal && (
        <div className="scope-assessment-modal-overlay">
          <div className="scope-assessment-confirm-modal">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="scope-assessment-modal-buttons">
              <button className="scope-assessment-modal-cancel" onClick={() => setShowLogoutModal(false)}>
                Cancel
              </button>
              <button className="scope-assessment-modal-confirm" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScopeStudentAssessment;